import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftRight, CloudSun, RefreshCw, Sun, Umbrella } from 'lucide-react';
import { useEffect, useState } from 'react';

type WeatherData = {
  temperature: number;
  weatherCode: number;
  isDay: boolean;
};

type WeatherCity = {
  name: string;
  latitude: number;
  longitude: number;
};

type ExchangeRates = Record<'EUR' | 'ZAR' | 'GBP' | 'MZN', number>;
type DisplayMode = 'usd' | 'weather' | 'eur' | 'zar' | 'gbp';

const displaySequence: DisplayMode[] = ['usd', 'weather', 'eur', 'weather', 'zar', 'weather', 'gbp', 'weather'];

const weatherFallback: WeatherData = {
  temperature: 25,
  weatherCode: 1,
  isDay: true,
};

const exchangeUrl = 'https://open.er-api.com/v6/latest/USD';
const exchangeFallback: ExchangeRates = { EUR: 0.86, ZAR: 15.97, GBP: 0.74, MZN: 63.82 };
const weatherCities: WeatherCity[] = [
  { name: 'Maputo', latitude: -25.9692, longitude: 32.5732 },
  { name: 'Matola', latitude: -25.9622, longitude: 32.4589 },
  { name: 'Beira', latitude: -19.8436, longitude: 34.8389 },
  { name: 'Nampula', latitude: -15.1165, longitude: 39.2666 },
  { name: 'Chimoio', latitude: -19.1164, longitude: 33.4833 },
  { name: 'Quelimane', latitude: -17.8786, longitude: 36.8883 },
  { name: 'Tete', latitude: -16.1564, longitude: 33.5867 },
  { name: 'Xai-Xai', latitude: -25.0519, longitude: 33.6442 },
  { name: 'Pemba', latitude: -12.9739, longitude: 40.5178 },
  { name: 'Inhambane', latitude: -23.865, longitude: 35.3833 },
  { name: 'Lichinga', latitude: -13.3128, longitude: 35.2406 },
];

const weatherUrl = (city: WeatherCity) =>
  `https://api.open-meteo.com/v1/forecast?latitude=${city.latitude}&longitude=${city.longitude}&current=temperature_2m,weather_code,is_day&timezone=Africa%2FMaputo`;

const weatherLabels: Record<number, string> = {
  0: 'Céu limpo',
  1: 'Pouco nublado',
  2: 'Parcialmente nublado',
  3: 'Nublado',
  45: 'Nevoeiro',
  48: 'Nevoeiro',
  51: 'Chuvisco',
  53: 'Chuvisco',
  55: 'Chuvisco',
  61: 'Chuva fraca',
  63: 'Chuva moderada',
  65: 'Chuva forte',
  80: 'Aguaceiros',
  81: 'Aguaceiros',
  82: 'Aguaceiros fortes',
  95: 'Trovoada',
};

function WeatherIcon({ weatherCode, isDay }: Pick<WeatherData, 'weatherCode' | 'isDay'>) {
  if (weatherCode >= 51) return <Umbrella size={24} strokeWidth={1.8} />;
  if (weatherCode >= 2) return <CloudSun size={25} strokeWidth={1.8} />;
  return <Sun size={25} strokeWidth={1.8} className={isDay ? 'text-amber-500' : 'text-slate-400'} />;
}

export function MarketWeatherTicker() {
  const [displayIndex, setDisplayIndex] = useState(0);
  const [exchangeRates, setExchangeRates] = useState(exchangeFallback);
  const [weatherByCity, setWeatherByCity] = useState<Record<string, WeatherData>>({});

  useEffect(() => {
    const interval = window.setInterval(() => {
      setDisplayIndex((index) => (index + 1) % displaySequence.length);
    }, 6000);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const loadData = async () => {
      const weatherRequests = weatherCities.map((city) => fetch(weatherUrl(city), { signal: controller.signal }));
      const [exchangeResult, ...weatherResults] = await Promise.allSettled([
        fetch(exchangeUrl, { signal: controller.signal }),
        ...weatherRequests,
      ]);

      if (exchangeResult.status === 'fulfilled' && exchangeResult.value.ok) {
        const exchangeData = await exchangeResult.value.json();
        const rates = exchangeData?.rates || {};
        const nextRates = { ...exchangeFallback };
        for (const currency of Object.keys(nextRates) as Array<keyof ExchangeRates>) {
          const rate = Number(rates[currency]);
          if (Number.isFinite(rate)) nextRates[currency] = rate;
        }
        setExchangeRates(nextRates);
      }

      const nextWeather: Record<string, WeatherData> = {};
      for (const [index, weatherResult] of weatherResults.entries()) {
        if (weatherResult.status !== 'fulfilled' || !weatherResult.value.ok) continue;
        const weatherData = await weatherResult.value.json();
        const current = weatherData?.current;
        if (current && Number.isFinite(Number(current.temperature_2m))) {
          nextWeather[weatherCities[index].name] = {
            temperature: Number(current.temperature_2m),
            weatherCode: Number(current.weather_code),
            isDay: Boolean(current.is_day),
          };
        }
      }
      setWeatherByCity((currentWeather) => ({ ...currentWeather, ...nextWeather }));
    };

    void loadData();
    const refreshTimer = window.setInterval(() => void loadData(), 10 * 60 * 1000);

    return () => {
      controller.abort();
      window.clearInterval(refreshTimer);
    };
  }, []);

  const displayMode = displaySequence[displayIndex];
  const showWeather = displayMode === 'weather';
  const panelLabel = showWeather ? 'Tempo' : 'Câmbio';
  const selectedCity = weatherCities[Math.floor(displayIndex / 2) % weatherCities.length];
  const weather = weatherByCity[selectedCity.name] || weatherFallback;

  const moveDisplay = (direction: 1 | -1) => {
    setDisplayIndex((index) => (index + direction + displaySequence.length) % displaySequence.length);
  };

  return (
    <div
      className="market-weather-ticker block w-[150px] sm:w-[178px] shrink-0"
      aria-label="Informação de câmbio e tempo"
      onPointerDown={(event) => {
        const startX = event.clientX;
        const handlePointerUp = (upEvent: PointerEvent) => {
          const distance = upEvent.clientX - startX;
          if (Math.abs(distance) > 35) moveDisplay(distance < 0 ? 1 : -1);
          window.removeEventListener('pointerup', handlePointerUp);
        };
        window.addEventListener('pointerup', handlePointerUp);
      }}
    >
      <div className="relative h-[52px] overflow-hidden rounded-xl border border-primary-200/70 bg-white/75 shadow-sm backdrop-blur-md dark:border-primary-800/60 dark:bg-dark-800/75">
        <AnimatePresence initial={false} mode="wait">
          <motion.div
            key={displayIndex}
            initial={{ opacity: 0, x: showWeather ? 28 : -28 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: showWeather ? -28 : 28 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.35}
            onDragEnd={(_, info) => {
              if (Math.abs(info.offset.x) > 30) moveDisplay(info.offset.x < 0 ? 1 : -1);
            }}
            className="flex h-full cursor-grab items-center justify-between gap-2 px-3 active:cursor-grabbing"
            aria-live="polite"
          >
            {showWeather ? (
              <>
                <div className="flex items-center gap-2 text-sky-600 dark:text-sky-300">
                  <WeatherIcon weatherCode={weather.weatherCode} isDay={weather.isDay} />
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">{selectedCity.name}</p>
                    <p className="text-sm font-semibold leading-tight text-gray-800 dark:text-white">
                      {Math.round(weather.temperature)}°C <span className="text-[11px] font-normal text-gray-500 dark:text-gray-400">{weatherLabels[weather.weatherCode] || 'Tempo atual'}</span>
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 text-primary-700 dark:text-primary-300">
                <ArrowLeftRight size={23} strokeWidth={1.8} />
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Câmbio</p>
                  <p className="text-sm font-semibold leading-tight text-gray-800 dark:text-white">
                    {displayMode === 'usd' && <>$1 = {exchangeRates.MZN.toFixed(2)} MZN</>}
                    {displayMode === 'eur' && <>€1 = {(exchangeRates.MZN / exchangeRates.EUR).toFixed(2)} MZN</>}
                    {displayMode === 'zar' && <>R1 = {(exchangeRates.MZN / exchangeRates.ZAR).toFixed(2)} MZN</>}
                    {displayMode === 'gbp' && <>£1 = {(exchangeRates.MZN / exchangeRates.GBP).toFixed(2)} MZN</>}
                  </p>
                </div>
              </div>
            )}
            <div className="flex shrink-0 flex-col items-center gap-1" aria-hidden="true">
              <RefreshCw size={12} className="text-gray-400" />
              <span className="text-[9px] text-gray-400">{panelLabel}</span>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="mt-1 flex justify-center gap-1" aria-label="Selecionar informação">
        {displaySequence.map((mode, index) => (
          <button
            key={`${mode}-${index}`}
            type="button"
            onClick={() => setDisplayIndex(index)}
            className={`h-1 rounded-full transition-all ${displayIndex === index ? 'w-3 bg-primary-500' : 'w-1 bg-gray-300 dark:bg-dark-600'}`}
            aria-label={`Mostrar ${mode === 'weather' ? 'tempo' : mode.toUpperCase()}`}
            aria-pressed={displayIndex === index}
          />
        ))}
      </div>
    </div>
  );
}
